import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import apiCoreAdapter from '../../hooks/apiCoreAdapter';
import { usePsychologistContext } from '../../contexts/PsychologistContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNzJourneyStatus } from '../../hooks/useNzJourneyStatus';
import { BA_TIMEZONE } from '../../utils/timezone';
import { toastUtils } from '../../hooks/useToast';
import { fetchNoScheduleMap, type NoScheduleEntry } from '../../utils/tallyNoSchedule';
import { useNoAgendamientoModal } from '../psychologist/patients/no-agendamiento/useNoAgendamientoModal';
import { NoAgendamientoModal } from '../psychologist/patients/no-agendamiento/NoAgendamientoModal';

import { activityTracker } from "../../utils/activityTracker";
interface ReportAbsenceModalProps {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  /** Scheduled start of the session, in the psychologist's own timezone (epoch ms). */
  sessionStartMs: number;
  patientName: string;
  /** Email del paciente — no siempre disponible (ej. la fila de Vista diaria no lo trae). */
  patientEmail?: string;
  /**
   * ID del proceso terapéutico de esta sesión — habilita el link precargado al
   * formulario de no agendamiento (Tally). Si no está disponible, esa sección
   * del modal no se muestra.
   */
  processId?: string;
}

/** Tolerancia institucional real (ver Home → "Duración, Puntualidad y Protocolo de
 * Conexión"): hasta el minuto 35 la sesión todavía puede realizarse por el tiempo
 * restante; después de eso se considera perdida. Un solo valor gatea tanto poder
 * confirmar la salida como poder cargar el formulario de no agendamiento — no hay
 * margen "de seguridad" adicional, la política ya es el límite. */
const TOLERANCE_MINUTES = 35;

/** "24/08/2026 10:00", en el huso de la psicóloga. */
function formatSessionDateTime(startMs: number, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('es-AR', {
    timeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(startMs));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')}`;
}

export const ReportAbsenceModal: React.FC<ReportAbsenceModalProps> = ({
  open,
  onClose,
  sessionId,
  sessionStartMs,
  patientName,
  patientEmail,
  processId,
}) => {
  const navigate = useNavigate();
  const noAgendamientoModal = useNoAgendamientoModal();
  const [now, setNow] = useState(() => Date.now());
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [noScheduleEntry, setNoScheduleEntry] = useState<NoScheduleEntry | null>(null);
  const [notifyingPatient, setNotifyingPatient] = useState(false);
  const { psychologist } = usePsychologistContext();
  const { user } = useAuth();
  // NZ (y O recién egresada de NZ sin confirmar el egreso) no tienen acceso a la Home: AppShell las
  // rebota de /home a la Agenda, así que el link al protocolo completo las sacaba del modal para nada.
  const { canAccessHome } = useNzJourneyStatus();

  // Live countdown while the modal is open — ticks every second so the "llevás
  // X min esperando" line stays accurate without the psychologist needing to
  // interact with anything.
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open]);

  // Every time the modal is opened, persist an absence-report attempt so the
  // team has an aggregate record. Best-effort: a failed log must not block the
  // institutional guidance from being shown.
  useEffect(() => {
    if (!open) return;
    apiCoreAdapter.reportSessionAbsence(sessionId).catch((error) => {
      console.error('No se pudo registrar el reporte de ausencia', error);
    });
  }, [open, sessionId]);

  // Nivel 2 de la integración con el formulario de no agendamiento (Tally): si
  // ya se completó para este proceso (desde acá o desde Pacientes), lo mostramos
  // en vez de pedirlo de nuevo. Reusa el mismo webhook de Make que ya consulta
  // MissingNextSessionModal — falla en silencio (mapa vacío) ante cualquier error,
  // igual que el resto de los consumidores de `fetchNoScheduleMap`.
  useEffect(() => {
    if (!open) { setNoScheduleEntry(null); return; }
    setNoScheduleEntry(null);
    if (!processId || !user?.email) return;
    let cancelled = false;
    fetchNoScheduleMap(user.email, new Date(sessionStartMs).toISOString()).then((map) => {
      if (!cancelled) setNoScheduleEntry(map.get(processId) ?? null);
    });
    return () => { cancelled = true; };
  }, [open, processId, user?.email, sessionStartMs]);

  const elapsedMin = Math.max(0, Math.floor((now - sessionStartMs) / 60000));
  const remainingMin = Math.max(0, TOLERANCE_MINUTES - elapsedMin);
  const dynamicLine =
    remainingMin > 0
      ? `Llevás ${elapsedMin} min esperando. Según la política institucional, la tolerancia llega hasta el minuto ${TOLERANCE_MINUTES} — te quedan ${remainingMin} min.`
      : `Ya pasaron los ${TOLERANCE_MINUTES} minutos de tolerancia institucional — la sesión se considera perdida y ya podés cerrarla.`;

  const canConfirmExit = elapsedMin >= TOLERANCE_MINUTES;

  const canOpenNoScheduleForm = elapsedMin >= TOLERANCE_MINUTES;

  // Paso 1 del protocolo: dispara el aviso automático al PACIENTE (psima le
  // manda el WhatsApp "psico_esperando_paciente" con los botones "Ya me
  // conecto"/"No podré asistir"). Nunca abre WhatsApp de la psicóloga: eso fue
  // lo que causaba el duplicado (ver psima_fix_reportar_ausencia) — el texto
  // libre que se le mandaba a CX matcheaba la detección de "Paciente no
  // ingresó" del lado de psima, y si la psicóloga además completaba ese flujo
  // viejo a mano, el paciente recibía el aviso dos veces.
  const handleNotifyPatient = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notifyingPatient) return;
    setNotifyingPatient(true);
    try {
      const { outcome } = await apiCoreAdapter.notifyPatientAbsence(sessionId);
      if (outcome === 'sent') {
        toastUtils.success(`Le avisamos a ${patientName} que lo estás esperando.`);
      } else if (outcome === 'already_sent') {
        toastUtils.info('Ya le avisamos al paciente.');
      } else if (outcome === 'error') {
        toastUtils.warning('No pudimos avisarle al paciente. Probá de nuevo en unos segundos.');
      }
      // 'skipped' (feature todavía apagada del lado de psima): sin toast, el
      // resto del flujo se ve exactamente igual.
    } catch {
      toastUtils.warning('No pudimos avisarle al paciente. Probá de nuevo en unos segundos.');
    } finally {
      setNotifyingPatient(false);
    }
  };

  const handleConfirmExit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canConfirmExit) return;
    setConfirmingExit(true);
    try {
      await apiCoreAdapter.confirmSessionAbsence(sessionId);
      activityTracker.trackAction('absence_reported');
      toastUtils.success('Confirmaste que saliste de la sesión.');
      onClose();
    } catch (error) {
      console.error('No se pudo confirmar el reporte de ausencia', error);
      toastUtils.error('No se pudo confirmar. Probá de nuevo en unos segundos.');
    } finally {
      setConfirmingExit(false);
    }
  };

  const noScheduleSubmittedLabel = noScheduleEntry?.submittedAt
    ? ` el ${formatSessionDateTime(new Date(noScheduleEntry.submittedAt).getTime(), psychologist?.timezone || BA_TIMEZONE)} hs`
    : '';

  return (
    <>
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        id="report-absence-modal"
        data-testid="report-absence-modal"
        className="sm:max-w-[40rem] p-0 overflow-hidden max-h-[92vh] flex flex-col"
      >
        <DialogHeader className="px-6 pt-5 pb-2 shrink-0">
          <DialogTitle className="text-[1.0625rem] font-semibold text-[#4a3d73]">
            ¿El paciente no se presentó y no avisó?
          </DialogTitle>
        </DialogHeader>

        {/* Más contenido que antes (CTA de WhatsApp, formulario de no agendamiento,
            link a la política) — con el header/footer fijos, esta sección scrollea
            en vez de empujar los botones de acción fuera del viewport. */}
        <div className="px-6 pb-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          <p
            data-testid="report-absence-countdown"
            className="text-sm font-semibold text-[#6D57BC] bg-[#f3efff] rounded-lg px-3 py-2"
          >
            {dynamicLine}
          </p>

          {/* Paso 1 del protocolo (0-10 min): avisarle al paciente es lo más urgente, por
              eso el CTA va primero y no como link secundario al pie del modal. Dispara
              directo el endpoint de psima — nunca abre WhatsApp de la psicóloga (ver
              handleNotifyPatient). El ícono (i) va AFUERA del botón (no anidado) para que
              hover/click sobre la ayuda nunca dispare el envío por accidente. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="report-absence-notify-patient-btn"
              disabled={notifyingPatient}
              onClick={handleNotifyPatient}
              className="flex-1 flex items-center justify-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700 hover:bg-green-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <MessageCircle className="h-4 w-4" />
              {notifyingPatient ? 'Solicitando...' : 'Solicitar aviso al paciente por WhatsApp'}
            </button>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="¿Cómo funciona este aviso?"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-green-200 text-green-700 hover:bg-green-50 transition-colors"
                >
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px] text-center">
                Al hacer click aquí, nos pondremos automáticamente en contacto con el paciente desde los canales institucionales — vos no tenés que escribirle. Te avisaremos apenas tengamos una respuesta.
              </TooltipContent>
            </Tooltip>
          </div>

          <div data-testid="report-absence-fixed-text" className="space-y-3">
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-gray-700 leading-snug text-justify">
              <li>Avisale al paciente de inmediato (botón de arriba) — CX le manda un WhatsApp automático desde los canales institucionales avisándole que lo estás esperando en la sala.</li>
              <li>Esperá en la sala de Meet hasta el minuto {TOLERANCE_MINUTES} desde el horario agendado — es el límite máximo de tolerancia institucional. Si el paciente se conecta antes, la sesión se realiza por el tiempo restante.</li>
              <li>Si el paciente ingresa tarde a la sesión, el horario de finalización sigue siendo el mismo que estaba pactado.</li>
              <li>Pasado el minuto {TOLERANCE_MINUTES} sin que se presente, completá el formulario de no agendamiento (más abajo) para que el paciente reciba el seguimiento automático correspondiente.</li>
            </ol>
            <p className="text-sm text-gray-600 leading-snug text-justify">
              <strong>Importante:</strong> la sesión se abona con normalidad. Vos cumpliste con tu horario — quien no se presentó fue el paciente.
            </p>
          </div>

          {processId && (
            <div data-testid="no-schedule-section">
              {noScheduleEntry ? (
                <p
                  data-testid="no-schedule-already-submitted"
                  className="text-sm font-medium text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                >
                  Ya completaste el formulario de no agendamiento para este proceso{noScheduleSubmittedLabel}.
                </p>
              ) : (
                <div className="space-y-1">
                  {canOpenNoScheduleForm ? (
                    <>
                      <button
                        type="button"
                        data-testid="open-no-schedule-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!processId) return;
                          onClose();
                          noAgendamientoModal.open({
                            processId,
                            patientEmail: patientEmail || '',
                            patientName,
                            psychologistEmail: user?.email || '',
                          });
                        }}
                        className="w-full rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors border-[#6D57BC] text-[#6D57BC] hover:bg-[#f3efff] cursor-pointer"
                      >
                        Completar formulario de no agendamiento
                      </button>
                      <p className="text-xs text-gray-500 px-1">
                        Paso 4 del protocolo — quedó pendiente por esta sesión.
                      </p>
                    </>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          data-testid="open-no-schedule-btn"
                          aria-disabled="true"
                          onClick={(e) => e.stopPropagation()}
                          className="w-full rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed"
                        >
                          Completar formulario de no agendamiento
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[260px] text-center">
                        Se habilita automáticamente al cumplirse el minuto {TOLERANCE_MINUTES}, cuando termina la tolerancia institucional.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          )}

          {canAccessHome && (
            <button
              type="button"
              data-testid="report-absence-policy-link"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/home?article=duracion-puntualidad-sesiones');
              }}
              className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2"
            >
              Ver protocolo completo en Home
            </button>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex flex-col sm:flex-row sm:justify-between gap-2 shrink-0">
          <button
            type="button"
            onClick={(e) => {
              // Radix' Dialog content renders through a portal to document.body, but
              // React still bubbles synthetic events along the *component* tree — not
              // the DOM tree. Without this, clicking here would also trigger whatever
              // onClick wraps the button/card that opened this modal (e.g. the day
              // timeline card), unintentionally opening SessionDetailsModal too.
              e.stopPropagation();
              onClose();
            }}
            data-testid="report-absence-close-btn"
            className="px-5 py-2 rounded-full text-sm font-semibold text-[#6D57BC] bg-transparent border border-[#6D57BC] cursor-pointer transition-all hover:bg-[#f3efff]"
          >
            Entendido
          </button>
          {/* Recién pasada la tolerancia: antes del minuto 35 no se puede salir, así que el botón
              ni se muestra (aparece solo, sin reabrir el modal, cuando el contador llega a 35). */}
          {canConfirmExit && (
            <button
              type="button"
              onClick={handleConfirmExit}
              disabled={confirmingExit}
              data-testid="confirm-exit-session-btn"
              className="px-5 py-2 rounded-full text-sm font-semibold text-white border-none cursor-pointer transition-all hover:brightness-90 disabled:opacity-60"
              style={{ background: '#6D57BC' }}
            >
              {confirmingExit ? 'Confirmando...' : 'Ya solicité aviso al paciente, salir de la sesión'}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <NoAgendamientoModal params={noAgendamientoModal.params} onClose={noAgendamientoModal.close} />
    </>
  );
};

export default ReportAbsenceModal;
